import { crdt, type Change, type Doc, type Edit, type RemoteUpdate, type Json } from '@w/crdt'
import { indexViewBlocks, type NewBlock } from './doc-blocks'
import { prepareQuestionMetadata } from './question-metadata'
import { canonicalBlocks } from './core/canonical-blocks'
import type { Blocks } from '@w/content-blocks'

export { pageDocument } from './doc-blocks'
export type { PageBlock, PageBlocks, PageDocument, TextSegment, NewBlock } from './doc-blocks'
export { getAffectedBlocks, classifyBlockDelta } from './view-delta'
export { writeDocument } from './write-document'
export type { Change, Doc, Edit, RemoteUpdate } from '@w/crdt'

/** A document whose blocks are a projection of something else — a training card,
 * a reviewed file — is still a document. This builds one from blocks alone, so a
 * surface that renders a projection never has to reach for the engine itself. */
export function docFromBlocks(blocks: readonly unknown[], title?: string): Doc {
  return crdt.create({ ...(title === undefined ? {} : { title }),
    content: { children: structuredClone(canonicalBlocks(blocks as Blocks)) as never } })
}

/** A second replica of the same document, for a surface whose content is
 * authored elsewhere and arrives as updates. */
export function cloneDoc(doc: Doc): Doc {
  return crdt.load(doc.encodeSnapshot().bytes)
}

export interface PageBindings {
  readonly doc: Doc
  onChange(change: Change): void
  applyRemote(update: RemoteUpdate): ReturnType<Doc['applyUpdate']>
}

/** The authored Change is the persistence unit. Imports never echo through onChange. */
export function createPageBindings(doc: Doc, onChange: (change: Change) => void): PageBindings {
  return { doc, onChange, applyRemote: update => doc.applyUpdate(update) }
}

/** One transaction includes structural/text edits and the question ordering they imply. */
export function editPage(bindings: PageBindings, edit: (tx: Edit) => void,
  options?: { group?: string; origin?: string }): Change {
  const change = bindings.doc.edit(tx => {
    edit(tx)
    prepareQuestionMetadata(tx)
  }, options)
  if (change.changed) bindings.onChange(change)
  return change
}

/** New subtree IDs come from insert/editText, including nested captions and question bodies. */
export function insertBlocks(tx: Edit, list: string, at: number, blocks: readonly NewBlock[]): readonly string[] {
  const ids: string[] = []
  for (const [offset, block] of blocks.entries()) {
    const nested: [string, NewBlock[]][] = []
    const fields: Record<string, Json> = {}
    for (const [key, value] of Object.entries(block)) {
      if (key === 'id') continue
      if (block.type === 'chars' && key === 'text') {
        if (typeof value === 'string') fields.text = []
        else if (Array.isArray(value) && value.every(segment => segment && typeof segment === 'object' &&
          !Array.isArray(segment) && typeof segment.id === 'string' && typeof segment.text === 'string' &&
          Object.keys(segment).length === 2)) fields.text = value
        else throw new Error('Expected { id, text } segments or new text')
        continue
      }
      if (Array.isArray(value) && value.length > 0 && value.every(item => isNewBlock(item))) {
        fields[key] = []
        nested.push([key, value])
      } else fields[key] = value
    }
    const [id] = tx.insert(list, at + offset, [fields])
    ids.push(id)
    const path = `${list}.${id}`
    for (const [key, children] of nested) insertBlocks(tx, `${path}.${key}`, 0, children)
    if (block.type === 'chars') {
      const text = block.text
      if (typeof text === 'string') tx.editText(`${path}.text`, 0, 0, text)
      else if (!Array.isArray(text)) throw new Error('Expected chars text')
    }
  }
  return ids
}

export function deleteBlocks(tx: Edit, ids: readonly string[]): void {
  for (const id of ids) {
    const location = indexViewBlocks(tx.read('')).get(id)
    if (location) tx.remove(location.list, location.index, 1)
  }
}

/** Preserve block, descendant and source identities across both sibling and parent changes. */
export function moveBlock(tx: Edit, id: string, list: string, at: number): void {
  const location = requireBlock(tx, id)
  tx.moveTo(location.list, location.index, list, at)
}

export function patchBlock(tx: Edit, id: string, fields: Record<string, Json | undefined>): void {
  if ('id' in fields) throw new Error('Block identity is engine-owned')
  const location = requireBlock(tx, id)
  tx.mergeMap(location.path, fields)
}

export function replaceBlocks(tx: Edit, id: string, blocks: readonly NewBlock[]): readonly string[] {
  const location = requireBlock(tx, id)
  tx.remove(location.list, location.index, 1)
  return insertBlocks(tx, location.list, location.index, blocks)
}

export function editChars(tx: Edit, id: string, offset: number, count: number, text: string): void {
  const location = requireBlock(tx, id)
  if (location.block.type !== 'chars') throw new Error(`Expected chars block ${id}`)
  tx.editText(`${location.path}.text`, offset, count, text)
}

function requireBlock(tx: Edit, id: string) {
  const location = indexViewBlocks(tx.read('')).get(id)
  if (!location) throw new Error(`Missing block ${id}`)
  return location
}

function isNewBlock(value: Json): value is NewBlock {
  return value !== null && typeof value === 'object' && !Array.isArray(value) && typeof value.type === 'string'
}
